/**
 * Mock Data Module
 * Provides type-safe mock data for demo/preview functionality
 * Following best practices: centralized data, type safety, realistic values
 */

import { Property } from '../components/Investment/PropertyCard';

import { getTenantId } from '../tenants';

/**
 * Mock Properties for Demo Portal
 * Realistic real estate investment properties with diverse characteristics
 */
export const MOCK_PROPERTIES: Property[] = [
  {
    id: 'demo-1',
    address: '742 Evergreen Terrace',
    city: 'Springfield',
    state: 'OR',
    price: 450000,
    tokenSupply: 1000,
    expectedRoi: 8.5,
    status: 'available',
    imageUrl: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format&fit=crop&q=60',
    propertyType: 'single-family',
    investors: 247,
  },
  {
    id: 'demo-2',
    address: '221B Baker Street',
    city: 'London',
    state: 'UK',
    price: 875000,
    tokenSupply: 2000,
    expectedRoi: 12.3,
    status: 'funded',
    imageUrl: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&auto=format&fit=crop&q=60',
    propertyType: 'multi-family',
    investors: 412,
  },
  {
    id: 'demo-3',
    address: '1640 Riverside Drive',
    city: 'Hill Valley',
    state: 'CA',
    price: 625000,
    tokenSupply: 1500,
    expectedRoi: 10.2,
    status: 'available',
    imageUrl: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&auto=format&fit=crop&q=60',
    propertyType: 'vacation-rental',
    investors: 189,
  },
  {
    id: 'demo-4',
    address: '1007 Mountain Drive',
    city: 'Gotham',
    state: 'NJ',
    price: 1250000,
    tokenSupply: 2500,
    expectedRoi: 9.8,
    status: 'sold_out',
    imageUrl: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800&auto=format&fit=crop&q=60',
    propertyType: 'luxury',
    investors: 589,
  },
  {
    id: 'demo-5',
    address: '124 Conch Street',
    city: 'Bikini Bottom',
    state: 'FL',
    price: 385000,
    tokenSupply: 800,
    expectedRoi: 7.9,
    status: 'available',
    imageUrl: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&auto=format&fit=crop&q=60',
    propertyType: 'beachfront',
    investors: 156,
  },
  {
    id: 'demo-6',
    address: '31 Spooner Street',
    city: 'Quahog',
    state: 'RI',
    price: 425000,
    tokenSupply: 950,
    expectedRoi: 8.8,
    status: 'available',
    imageUrl: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&auto=format&fit=crop&q=60',
    propertyType: 'single-family',
    investors: 203,
  },
];

/**
 * Mock Portfolio Data
 * User's investment portfolio with performance metrics
 */
export interface PortfolioHolding {
  propertyId: string;
  propertyName: string;
  sharesOwned: number;
  currentValue: number;
  initialInvestment: number;
  returnPercentage: number;
  lastDividend: number;
  dividendYield: number;
}

export const MOCK_PORTFOLIO: PortfolioHolding[] = [
  {
    propertyId: 'demo-1',
    propertyName: '742 Evergreen Terrace',
    sharesOwned: 50,
    currentValue: 24750,
    initialInvestment: 22500,
    returnPercentage: 10.0,
    lastDividend: 125.50,
    dividendYield: 6.1,
  },
  {
    propertyId: 'demo-2',
    propertyName: '221B Baker Street',
    sharesOwned: 25,
    currentValue: 11875,
    initialInvestment: 10937.50,
    returnPercentage: 8.6,
    lastDividend: 89.25,
    dividendYield: 9.0,
  },
  {
    propertyId: 'demo-3',
    propertyName: '1640 Riverside Drive',
    sharesOwned: 35,
    currentValue: 15400,
    initialInvestment: 14583.33,
    returnPercentage: 5.6,
    lastDividend: 98.75,
    dividendYield: 7.7,
  },
];

/**
 * Mock Portfolio Summary Metrics
 */
export interface PortfolioSummary {
  totalValue: number;
  totalInvestment: number;
  totalReturn: number;
  totalReturnPercentage: number;
  totalDividends: number;
  activeProperties: number;
  monthlyIncome: number;
  annualizedReturn: number;
}

export const MOCK_PORTFOLIO_SUMMARY: PortfolioSummary = {
  totalValue: 52025,
  totalInvestment: 48020.83,
  totalReturn: 4004.17,
  totalReturnPercentage: 8.34,
  totalDividends: 1247.65,
  activeProperties: 3,
  monthlyIncome: 313.50,
  annualizedReturn: 7.8,
};

/**
 * Mock User Profile
 */
export interface DemoUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  kycStatus: 'verified' | 'pending' | 'not_started';
  accountBalance: number;
  joinedDate: string;
  totalInvested: number;
  portfolioValue: number;
}

export const MOCK_USER: DemoUser = getTenantId() === 'redex'
  ? {
      id: 'demo-user-123',
      email: 'demo@redexct.xyz',
      firstName: 'Demo',
      lastName: 'Investor',
      kycStatus: 'verified',
      accountBalance: 25000,
      joinedDate: '2025-06-01',
      totalInvested: 325000,
      portfolioValue: 350500,
    }
  : {
      id: 'demo-user-123',
      email: 'demo@honeyshares.com',
      firstName: 'Demo',
      lastName: 'Investor',
      kycStatus: 'verified',
      accountBalance: 15000,
      joinedDate: '2024-01-15',
      totalInvested: 48020.83,
      portfolioValue: 52025,
    };

/**
 * Helper function to get property by ID
 */
export const getMockPropertyById = (id: string): Property | undefined => {
  return MOCK_PROPERTIES.find(property => property.id === id);
};

/**
 * Helper function to filter properties by status
 */
export const getMockPropertiesByStatus = (status: Property['status']): Property[] => {
  return MOCK_PROPERTIES.filter(property => property.status === status);
};

/**
 * Helper function to get available properties
 */
export const getAvailableMockProperties = (): Property[] => {
  return getMockPropertiesByStatus('available');
};
